import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { Button } from '~/components/ui/buttons/Button';
import { WarningBanner } from '~/components/ui/Banner';
import { BundleQuantityLabel } from '~/components/ui/BundleQuantityLabel';
import { ProductLengthQuantityFields } from '~/components/common/ProductLengthQuantityFields';
import { useProductLengthPick } from '~/components/common/useProductLengthPick';
import {
  saveCompletePick,
  type InitialProductLengthPick,
  type PickedProductLength,
} from '~/components/common/productLengthChoice';
import {
  useSetCannedTallyOnBuyList,
  useAddProductToBuyList,
  useSetRandomTallyOnBuyList,
} from '~/api/buyList';
import { bundleCountsOf } from '~/components/common/tallyBundleCounts';
import {
  findSavedTally,
  type SavedTallyKey,
} from '~/components/common/savedTallies';
import { usePickedProductLength } from '~/hooks/usePickedProductLength';
import { toastGraphQLError } from '~/utils';
import type { BuyListDetailTally } from './tallies/types';
import type { BuyListDetailItem } from './types';

type BuyListAddProductRowProps = {
  buyListId: string;
  transportVehicleId: string;
  items: BuyListDetailItem[];
  tallies: BuyListDetailTally[];
  platformProductLengthIdsHiddenInSellerQuotes: ReadonlySet<string>;
  initialPick?: InitialProductLengthPick;
  onSavingChange: (isSaving: boolean) => void;
  onPendingPickChange: (hasPendingPick: boolean) => void;
};

function cannedTallyCounts(tallies: BuyListDetailTally[]) {
  return Object.fromEntries(
    tallies.flatMap((tally) =>
      tally.__typename === 'CannedBuyListTally'
        ? [[tally.cannedTally.id, tally.tallyCount]]
        : []
    )
  );
}

function randomBundlesByProduct(tallies: BuyListDetailTally[]) {
  return Object.fromEntries(
    tallies
      .filter((tally) => tally.__typename === 'RandomBuyListTally')
      .map((tally) => [tally.platformProduct.id, bundleCountsOf(tally.bundles)])
  );
}

function buyListTallyKey(tally: BuyListDetailTally): SavedTallyKey {
  return {
    platformProductId: tally.platformProduct.id,
    cannedTallyId:
      tally.__typename === 'CannedBuyListTally' ? tally.cannedTally.id : null,
  };
}

export function BuyListAddProductRow({
  buyListId,
  transportVehicleId,
  items,
  tallies,
  platformProductLengthIdsHiddenInSellerQuotes,
  initialPick,
  onSavingChange,
  onPendingPickChange,
}: Readonly<BuyListAddProductRowProps>) {
  const { t } = useTranslation('inventory');
  const { pick, fields, resetKey, reset } = useProductLengthPick({
    transportVehicleId,
    initialPick,
    onCompleteChange: onPendingPickChange,
    pickSeveralLengths: true,
  });
  const {
    existingItem: savedItem,
    existingLineItems,
    replaceNoticeId,
  } = usePickedProductLength(
    pick,
    items,
    (item) => item.platformProductsLength.id
  );
  const {
    mutate: addProductToBuyList,
    mutateAsync: addProductToBuyListAsync,
    isPending: isAddingProduct,
  } = useAddProductToBuyList();
  const { mutate: addCannedTally, isPending: isAddingCannedTally } =
    useSetCannedTallyOnBuyList();
  const { mutate: setRandomTally, isPending: isSettingRandomTally } =
    useSetRandomTallyOnBuyList();
  const [isAddingLines, setIsAddingLines] = useState(false);
  const isPending =
    isAddingLines ||
    isAddingProduct ||
    isAddingCannedTally ||
    isSettingRandomTally;
  const existingCannedTallyCounts = useMemo(
    () => cannedTallyCounts(tallies),
    [tallies]
  );
  const existingRandomBundles = useMemo(
    () => randomBundlesByProduct(tallies),
    [tallies]
  );
  const savedTally = findSavedTally(tallies, buyListTallyKey, pick);
  const savedLineItems = isAddingLines ? [] : existingLineItems;
  const hasReplaceNotice =
    !!savedItem || !!savedTally || savedLineItems.length > 0;

  useEffect(() => onSavingChange(isPending), [isPending, onSavingChange]);

  const tallyCallbacks = {
    onSuccess: () => {
      toast.success(t('inventory.buy_list.tallies.save_success'));
      reset();
    },
    onError: toastGraphQLError,
  };

  const confirmAddedLengths = (lengthIds: ReadonlySet<string>) =>
    toast.success(
      t(
        [...lengthIds].some((lengthId) =>
          platformProductLengthIdsHiddenInSellerQuotes.has(lengthId)
        )
          ? 'inventory.buy_list.add_lengths_success_in_seller_quotes'
          : 'inventory.buy_list.add_lengths_success',
        {
          count: lengthIds.size,
          show: t('inventory.buy_list.show_items_in_seller_quotes'),
        }
      )
    );

  const addLines = async (lines: PickedProductLength[]) => {
    setIsAddingLines(true);
    const addedLengthIds = new Set<string>();
    try {
      for (const line of lines) {
        await addProductToBuyListAsync({
          buyListId,
          platformProductsLengthId: line.platformProductsLength.id,
          quantity: line.quantity,
        });
        addedLengthIds.add(line.platformProductsLength.id);
      }
    } catch (error) {
      if (addedLengthIds.size > 0) {
        confirmAddedLengths(addedLengthIds);
        fields.dropLengths(addedLengthIds);
      }
      toastGraphQLError(error);
      return;
    } finally {
      setIsAddingLines(false);
    }
    confirmAddedLengths(addedLengthIds);
    reset();
  };

  const add = () =>
    saveCompletePick(pick, {
      lengths: (lengthsPick) => void addLines(lengthsPick.lines),
      canned: (cannedPick) =>
        addCannedTally(
          {
            buyListId,
            cannedTallyId: cannedPick.cannedTally.id,
            tallyCount: cannedPick.tallyCount,
          },
          tallyCallbacks
        ),
      random: (randomPick) =>
        setRandomTally(
          {
            buyListId,
            platformProductId: randomPick.platformProduct.id,
            bundles: randomPick.bundles,
          },
          tallyCallbacks
        ),
      length: (lengthPick) =>
        addProductToBuyList(
          {
            buyListId,
            platformProductsLengthId: lengthPick.platformProductsLength.id,
            quantity: lengthPick.quantity,
          },
          {
            onSuccess: () => {
              toast.success(
                t(
                  platformProductLengthIdsHiddenInSellerQuotes.has(
                    lengthPick.platformProductsLength.id
                  )
                    ? 'inventory.buy_list.add_success_in_seller_quotes'
                    : 'inventory.buy_list.add_success',
                  { show: t('inventory.buy_list.show_items_in_seller_quotes') }
                )
              );
              reset();
            },
            onError: toastGraphQLError,
          }
        ),
    });

  const renderReplaceNotice = () => {
    if (savedLineItems.length > 0) {
      return (
        <WarningBanner
          className="mt-2"
          testId="buy-list-add-product-lengths-replace-notice"
        >
          {t('inventory.buy_list.replaces_saved_lengths', {
            count: savedLineItems.length,
            lengths: savedLineItems
              .map((item) => item.platformProductsLength.lengthDisplayValue)
              .join(', '),
          })}
        </WarningBanner>
      );
    }
    if (savedTally && pick.kind === 'canned') {
      return (
        <WarningBanner
          className="mt-2"
          testId="buy-list-add-product-canned-replace-notice"
        >
          {t('inventory.buy_list.tallies.replaces_canned_count')}
        </WarningBanner>
      );
    }
    if (savedTally) {
      return (
        <WarningBanner
          className="mt-2"
          testId="buy-list-add-product-random-replace-notice"
        >
          {t('inventory.buy_list.tallies.replaces_random_bundles')}
        </WarningBanner>
      );
    }
    if (savedItem) {
      return (
        <WarningBanner
          className="mt-2"
          testId="buy-list-add-product-replace-notice"
        >
          {t('inventory.buy_list.replaces_saved_quantity')}
          <BundleQuantityLabel
            quantity={savedItem.quantity}
            piecesPerBundle={savedItem.effectivePiecesPerBundle}
            platformProductsLength={savedItem.platformProductsLength}
            align="start"
            className="mt-1"
          />
        </WarningBanner>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col border-b border-neut-border p-4">
      <ProductLengthQuantityFields
        key={resetKey}
        testId="buy-list-add-product"
        fields={fields}
        existingCannedTallyCount={(cannedTally) =>
          existingCannedTallyCounts[cannedTally.id]
        }
        existingRandomBundles={existingRandomBundles}
        disabled={isPending}
      >
        <div className="shrink-0 pt-5">
          <Button
            variant="primary"
            size="sm"
            width="auto"
            disabled={!pick.isComplete || isPending}
            onClick={add}
            aria-describedby={hasReplaceNotice ? replaceNoticeId : undefined}
            data-testid="buy-list-add-product-submit"
          >
            <Plus size={14} aria-hidden="true" />
            {pick.kind === 'lengths' && pick.isComplete
              ? t('inventory.buy_list.add_lengths', {
                  count: pick.lines.length,
                })
              : t('inventory.buy_list.add_product')}
          </Button>
        </div>
      </ProductLengthQuantityFields>
      <div id={replaceNoticeId} role="status">
        {renderReplaceNotice()}
      </div>
    </div>
  );
}
